/**
 * Logging a block to Autotask (section 10.1; ADRs 0050 and 0051): the form,
 * the preview behind it, and the submission's progress.
 *
 * - With no submission yet, a ready block shows the form. Every change is
 *   previewed, so what's missing, or why nothing would be written here, shows
 *   before the click.
 * - Once submitted, the block shows each step as it's written, and Autotask's
 *   own words if it refuses, with Try again (section 10.6).
 * - Once logged, whether its ticket is open or closed (v0.9.0's P2b): "Logged
 *   to Autotask: ticket T… · open", its status and age in Autotask, or who
 *   closed it and when; and, while it's open, **Close ticket** (P3).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useId, useRef, useState, useSyncExternalStore } from "react";

import { ApiError } from "../../api/client";
import type { Submission, SubmissionPreview, SubmitChoices } from "../../api/types";
import { useSession } from "../../app/session-context";
import { usePrivacy } from "../../lib/privacy";
import { formatShortDay, todayIn } from "../../lib/dates";
import { formatDuration } from "../../lib/durations";
import { CloseTicket } from "./CloseTicket";
import { useDayFacts } from "./dayContext";
import { useDrafts } from "./drafts-context";
import type { BlockView } from "./model";
import { TicketField } from "./TicketField";
import { isUnfinished, ticketActionQuery, useTicketAction } from "./ticketActions";
import { LINE_STATES, type TicketView, canClose, openFor, ticketLine } from "./tickets";
import { useSentHere } from "./sentHere";
import { Combobox } from "../../components/ui/Combobox";

type Billing = NonNullable<SubmitChoices["billing"]>;
type ContractChoice = NonNullable<SubmitChoices["contract"]> | null;
type ContactChoice = NonNullable<SubmitChoices["contact"]> | null;

const PREVIEW_DELAY_MS = 300;
const POLL_MS = 2_000;
const inputClass =
  "w-full rounded-input border border-line bg-card px-3 text-[13.5px] text-text";

const BILLING: { value: Billing; label: string }[] = [
  { value: "managed", label: "Managed (S-Flat)" },
  { value: "hourly_remote", label: "Hourly, remote (C-Hourly Remote)" },
  { value: "hourly_onsite", label: "Hourly, on-site (C-Hourly On-site)" },
];

const WHERE_WRITES_GO: Record<Submission["write_mode"], string | null> = {
  off: "Writes to Autotask are off here: this shows what would be written, and nothing is sent.",
  allowlist: "Writes here go only to the companies on this environment's allowlist.",
  on: null,
};

// Contract dates are days: shown as Autotask has them, whatever the time zone.
const contractDay = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" });

/** `value` once it has stayed the same for `delay` ms, compared by content. */
function useDebounced<T>(value: T, delay: number): T {
  const key = JSON.stringify(value);
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(JSON.parse(key) as T);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [key, delay]);
  return settled;
}

/** Once more for a server error or no answer; never for a refusal (4xx). */
function retryOnce(failures: number, error: Error): boolean {
  return !(error instanceof ApiError && error.status < 500) && failures < 1;
}

/**
 * Where a block's Log form starts when it was dropped on an open ticket
 * (v0.9.0's P4), or a logged block's "Log a Ready block here" chose it (ADR
 * 0194): its Ticket field on that ticket. Nothing is written until the tech
 * logs it.
 */
export interface LogStart {
  blockId: string;
  ticketId: number;
  /** As techs know it (T20261002.0018); null when it isn't known. */
  number: string | null;
  /** Known closed: the form offers "Reopen and log" (P9; ADR 0197). */
  closed: boolean;
}

function SubmitForm({
  block,
  timeZone,
  droppedOn,
  ticketListOpen,
  onSubmitted,
  onReopening,
  onActionsResize,
}: {
  block: BlockView;
  timeZone: string;
  /** Dropped on an open ticket: it starts there (v0.9.0's P4). */
  droppedOn: LogStart | undefined;
  /** The Ticket field opens at once, on its search (ADR 0194). */
  ticketListOpen: boolean;
  onSubmitted: (s: Submission) => void;
  /** Reopen and log asked for: the worker logs it once it's open (#830). */
  onReopening: () => void;
  onActionsResize: ((height: number) => void) | undefined;
}) {
  const { api } = useSession();
  const titleId = useId();
  const notesId = useId();
  const notesHintId = useId();
  const closeHintId = useId();
  const billingId = useId();
  const contractId = useId();
  // Work that continues a logged block starts on that block's ticket (ADR
  // 0079), while it takes new work (v0.9.0's P4); work dropped on an open
  // ticket starts on that one.
  const continued = block.continuesTicket;
  const first = droppedOn ?? continued;
  const [mode, setMode] = useState<SubmitChoices["mode"]>(
    first ? "existing_ticket" : "new_ticket",
  );
  // Its number, as techs know it, where Autotask's was read back.
  const [ticket, setTicket] = useState(first ? (first.number ?? String(first.ticketId)) : "");
  const dropped = droppedOn ? (droppedOn.number ?? String(droppedOn.ticketId)) : null;
  // What the tech typed: kept across the form unmounting (#218), and saved on
  // the block, so a reload or another device starts from it (ADR 0097).
  const drafts = useDrafts();
  const [start] = useState(() => drafts.start(block.id, block.draft));
  const [title, setTitle] = useState<string | null>(start.title);
  const [notes, setNotes] = useState<string | null>(start.notes);
  const unsaved = useSyncExternalStore(drafts.subscribe, () => drafts.failed(block.id));
  const [billing, setBilling] = useState<Billing | null>(null);
  // Nothing chosen: the suggestion (ADR 0075).
  const [contract, setContract] = useState<ContractChoice>(null);
  // Nothing chosen: the contact the block's calls matched (BUILD_PLAN 10.2).
  const [contact, setContact] = useState<ContactChoice>(null);
  const [close, setClose] = useState<boolean | null>(null);
  // Made once: a second click sends the same key, and gets the same submission.
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  // Digits are the ticket's ID (as the open tickets list gives it); anything
  // else is its number, as techs know it (T20261002.0018; ADR 0076), which the
  // preview checks.
  const typed = ticket.trim();
  const typedId = /^\d+$/.test(typed) ? Number(typed) : null;
  const typedNumber = typedId === null && typed !== "" ? typed.toUpperCase() : null;
  // What the form says now. The preview follows it after a short pause. This
  // is what's sent, and only once the preview has caught up with it: a click
  // in the pause used to send what the form said before (found end to end).
  const current: SubmitChoices = {
    mode,
    existing_ticket_id: mode === "existing_ticket" ? typedId : null,
    existing_ticket_number: mode === "existing_ticket" ? typedNumber : null,
    title,
    notes,
    billing,
    contract,
    // A new ticket's only: an existing ticket keeps its own (the owner).
    contact: mode === "new_ticket" ? contact : null,
    close,
  };
  const choices = useDebounced<SubmitChoices>(current, PREVIEW_DELAY_MS);
  const caughtUp = JSON.stringify(choices) === JSON.stringify(current);
  const preview = useQuery({
    queryKey: ["submission-preview", block.id, choices],
    queryFn: () => api.previewSubmission(block.id, choices),
    placeholderData: keepPreviousData,
    // Once more for a server error, then say so (#217): not three times over
    // about 7 s with a blank form.
    retry: retryOnce,
  });
  // The client's open tickets, from Autotask: read once the Ticket field is
  // opened, or a ticket is chosen, never for a new ticket alone.
  const [listWanted, setListWanted] = useState(ticketListOpen);
  const openTickets = useQuery({
    queryKey: ["open-tickets", block.clientId],
    queryFn: () => api.listOpenTickets(block.clientId ?? ""),
    enabled: (mode === "existing_ticket" || listWanted) && block.clientId !== null,
    staleTime: 60_000,
  });
  const { now, tickets: trayTickets, loggedToday } = useDayFacts();
  // "logged by you", and the tickets it may start on though they aren't listed.
  const mine = new Set(
    trayTickets.filter((t) => t.reasons.includes("logged_axon_time")).map((t) => t.autotask_ticket_id),
  );
  const known = [
    ...(continued ? [{ ticketId: continued.ticketId, number: continued.number, tag: "continues this work" }] : []),
    ...(droppedOn ? [{ ticketId: droppedOn.ticketId, number: droppedOn.number }] : []),
  ];
  const queryClient = useQueryClient();
  const submit = useMutation({
    mutationFn: (sent: SubmitChoices) => api.submitBlock(block.id, sent, idempotencyKey),
    onSuccess: (created) => {
      drafts.clear(block.id);
      onSubmitted(created);
    },
    onError: (error) => {
      // Something changed since the preview (#217): show what's true now.
      if (error instanceof ApiError && error.status === 422) {
        void queryClient.invalidateQueries({ queryKey: ["submission-preview", block.id] });
      }
    },
  });

  // A closed ticket chosen: "Reopen and log" (P9; ADR 0197). The preview names
  // it; logging to it stays refused until it's open, so the reopen goes first,
  // carrying the form as it is when clicked: the worker logs the block that
  // way once the ticket is open, whatever the form says meanwhile, and
  // whether or not the popup is still open (#830).
  // One key a ticket: a second click on it is the same reopen.
  const reopenKeys = useRef(new Map<string, string>());
  // The reopen asked for, and the ticket it's for, as the Ticket field had it.
  const [asked, setAsked] = useState<{ actionId: string; ticket: string } | null>(null);
  // Another ticket chosen since: that reopen isn't this one's (#830).
  const reopenId = asked !== null && asked.ticket === ticket.trim() ? asked.actionId : null;
  const reopenAction = useTicketAction(reopenId);
  const reopen = useMutation({
    mutationFn: (sent: { ticketId: number; choices: SubmitChoices; ticket: string }) => {
      const key = reopenKeys.current.get(sent.ticket) ?? crypto.randomUUID();
      reopenKeys.current.set(sent.ticket, key);
      return api.reopenTicket(sent.ticketId, block.id, key, {
        choices: sent.choices,
        idempotency_key: idempotencyKey,
      });
    },
    onSuccess: (action, sent) => {
      queryClient.setQueryData(ticketActionQuery(action.id), action);
      setAsked({ actionId: action.id, ticket: sent.ticket });
      onReopening();
    },
  });
  const retryReopen = useMutation({
    mutationFn: (actionId: string) => api.retryTicketAction(actionId),
    onSuccess: (action) => {
      queryClient.setQueryData(ticketActionQuery(action.id), action);
    },
  });
  // Done: the worker starts the log, and says so on the reopen, a moment after
  // it's done; read it again until it has (#830).
  const finished = reopenAction.data?.state === "done" ? reopenAction.data : null;
  const logStarted = finished?.then_log_submission_id ?? null;
  const logRefused = finished?.then_log_error ?? null;
  const refetchReopen = reopenAction.refetch;
  useEffect(() => {
    if (finished === null) {
      return;
    }
    // Open again: the day's ticket reads open, and the tray too.
    void queryClient.invalidateQueries({ queryKey: ["day"] });
    void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    if (logStarted !== null) {
      // The popup follows its logging, as a log sent from it.
      void queryClient.invalidateQueries({ queryKey: ["submission", block.id] });
      return;
    }
    if (logRefused !== null) {
      // Open now: the form logs to it as to any ticket.
      void queryClient.invalidateQueries({ queryKey: ["submission-preview", block.id] });
      return;
    }
    const again = window.setTimeout(() => {
      void refetchReopen();
    }, 1000);
    return () => {
      window.clearTimeout(again);
    };
  }, [finished, logStarted, logRefused, queryClient, block.id, refetchReopen]);

  const data = preview.data;
  const where = data ? WHERE_WRITES_GO[data.write_mode] : null;
  const settled = caughtUp && !preview.isFetching && data !== undefined;
  const ready = settled && data.plan !== null && data.refusal === null && data.problems.length === 0;
  const closedTicket = data?.closed_ticket ?? null;
  const closing = close ?? data?.close ?? true;
  // Its "is closed" or "being reopened" problem is the note below, as the
  // preview words it; any other still stops it.
  const problems = (data?.problems ?? []).filter((problem) => problem !== closedTicket?.problem);
  // Reopening, or reopened and the worker starting the log.
  const reopening =
    reopenId !== null &&
    (reopenAction.data === undefined ||
      isUnfinished(reopenAction.data) ||
      (finished !== null && logStarted === null && logRefused === null));
  const reopenFailed = reopenAction.data?.state === "failed" ? reopenAction.data : null;
  const reopenable =
    settled && closedTicket?.state === "closed" && data.refusal === null && problems.length === 0;
  // The contract shown isn't one time can go on that day: the client's
  // default, kept as the choice until another is chosen (ADR 0101).
  const shownContract = contract ?? data?.contract_id ?? null;
  const contractUnusable =
    typeof shownContract === "number" &&
    !(data?.contracts ?? []).some((c) => c.autotask_contract_id === shownContract);
  // The button names it as the open tickets list does: by its number.
  const chosenTicket =
    typedId !== null
      ? (openTickets.data?.find((t) => t.ticket_id === typedId)?.number ??
        (continued?.ticketId === typedId ? continued.number : null) ??
        (droppedOn?.ticketId === typedId ? droppedOn.number : null) ??
        String(typedId))
      : typedNumber;
  // The button says what it will do (ADR 0194): "Log 28m to T…".
  const closedName = closedTicket ? (closedTicket.ticket_number ?? String(closedTicket.autotask_ticket_id)) : null;
  const label =
    mode === "new_ticket"
      ? `Create ticket & log ${block.duration}`
      : chosenTicket === null
        ? `Log ${block.duration} to a ticket`
        : closedName !== null || reopenId !== null
          ? `Reopen and log ${block.duration} to ${closedName ?? chosenTicket}`
          : `Log ${block.duration} to ${chosenTicket}`;
  // One line about an existing ticket: its status and age, and the time on it
  // that day, when it's the tech's; that it's open in Autotask, when it's
  // listed; or that a typed number is looked up first.
  const trayTicket = trayTickets.find((t) =>
    typedId !== null ? t.autotask_ticket_id === typedId : typedNumber !== null && t.ticket_number?.toUpperCase() === typedNumber,
  );
  const listedTicket = openTickets.data?.find((t) =>
    typedId !== null ? t.ticket_id === typedId : typedNumber !== null && t.number?.toUpperCase() === typedNumber,
  );
  let ticketInfo: string | null = null;
  if (mode === "existing_ticket" && typed !== "") {
    if (trayTicket !== undefined) {
      const today = loggedToday(trayTicket.autotask_ticket_id);
      ticketInfo =
        [
          trayTicket.status_label,
          trayTicket.age_from === null ? null : openFor(trayTicket.age_from, now, timeZone),
          today > 0 ? `${formatDuration(today)} logged today` : null,
        ]
          .filter((part): part is string => part !== null)
          .join(" · ") || null;
    } else if (listedTicket !== undefined) {
      ticketInfo = `Open in Autotask${listedTicket.created === null ? "" : ` · ${openFor(listedTicket.created, now, timeZone)}`}`;
    } else if (typedNumber !== null && !known.some((k) => k.number?.toUpperCase() === typedNumber)) {
      ticketInfo = "Typed in: Axon Time looks it up in Autotask before it logs.";
    }
  }

  // The actions' height, as it changes (a refusal, what's missing), so the
  // panel can keep a field the tech tabs to clear of them (#222).
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const actions = actionsRef.current;
    if (actions === null || onActionsResize === undefined || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      onActionsResize(Math.ceil(actions.getBoundingClientRect().height));
    });
    observer.observe(actions);
    return () => {
      observer.disconnect();
      onActionsResize(0);
    };
  }, [onActionsResize]);

  return (
    <form
      aria-label="Log to Autotask"
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) {
          submit.mutate(current);
        } else if (reopenable && reopenId === null && !reopen.isPending) {
          reopen.mutate({ ticketId: closedTicket.autotask_ticket_id, choices: current, ticket: ticket.trim() });
        }
      }}
    >
      {where ? (
        <p
          role="status"
          aria-label="Where writes go"
          className="rounded-row border border-dashed border-line px-3 py-2 text-[12.5px] text-muted"
        >
          {where}
        </p>
      ) : null}
      {/* Where its time goes: a new ticket, or one of the client's open ones
          (ADR 0194 decision 1). */}
      <TicketField
        clientName={block.title}
        isHipaa={block.isHipaa}
        tickets={openTickets.data}
        failed={openTickets.isError}
        value={mode === "new_ticket" ? "new" : ticket.trim()}
        onPick={(picked) => {
          if (picked === "new") {
            setMode("new_ticket");
          } else {
            setMode("existing_ticket");
            setTicket(picked);
          }
        }}
        known={known}
        mine={mine}
        now={now}
        timeZone={timeZone}
        initialOpen={ticketListOpen}
        onOpen={() => {
          setListWanted(true);
        }}
      />
      {mode === "new_ticket" ? (
        <div className="flex flex-col gap-[5px]">
          <label htmlFor={titleId} className="text-xs font-semibold text-muted">
            Ticket title
          </label>
          <input
            id={titleId}
            value={title ?? data?.suggested_title ?? ""}
            onChange={(event) => {
              setTitle(event.target.value);
              drafts.set(block.id, { title: event.target.value });
            }}
            onBlur={() => {
              drafts.flush(block.id);
            }}
            className={`${inputClass} h-10`}
          />
          <div className="mt-1.5 flex flex-col gap-1">
            <Combobox
              label="Contact"
              options={[
                { value: "none", label: "No contact" },
                ...(data?.contacts ?? []).map((c) => ({
                  value: String(c.autotask_contact_id),
                  label: c.name,
                })),
              ]}
              value={
                contact === null
                  ? data
                    ? data.contact_id === null
                      ? "none"
                      : String(data.contact_id)
                    : ""
                  : String(contact)
              }
              placeholder="One of the client's contacts"
              onChange={(value) => {
                // × clears it: no contact, in the same place (ADR 0078).
                setContact(value === "" || value === "none" ? "none" : Number(value));
              }}
            />
            <p className="text-[12px] text-muted">
              Who the ticket is for. It starts on the caller when one of the client&apos;s
              contacts called.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* One line about the ticket chosen, in place of title and contact. */}
          {ticketInfo === null ? null : (
            <p data-part="ticket-info" className="-mt-1 text-[12.5px] leading-[1.4] text-muted">
              {ticketInfo}
            </p>
          )}
          {closedTicket !== null ? (
            <ClosedNote ticket={closedTicket} timeZone={timeZone} />
          ) : dropped !== null ? (
            // Said while that's still the ticket chosen (the mockup's). A
            // closed one waits for the preview's closed note instead.
            ticket.trim() === dropped && droppedOn?.closed !== true ? (
              <p className="rounded-row bg-brand-tint px-3 py-2 text-[13px] text-brand-ink-on-tint">
                {`You dropped it on ticket ${dropped} (open), so it starts on that ticket. Nothing is written until you log it.`}
              </p>
            ) : null
          ) : continued && ticket.trim() === (continued.number ?? String(continued.ticketId)) ? (
            <p className="rounded-row bg-brand-tint px-3 py-2 text-[13px] text-brand-ink-on-tint">
              {`This continues work logged to ticket ${continued.number ?? String(continued.ticketId)}` +
                (canClose(continued.state) ? ", which is still open" : "") +
                ", so it starts on that ticket."}
            </p>
          ) : null}
        </>
      )}
      <div className="flex flex-col gap-[5px]">
        <label htmlFor={notesId} className="text-xs font-semibold text-muted">
          Notes
        </label>
        <textarea
          id={notesId}
          aria-describedby={notesHintId}
          rows={3}
          value={notes ?? data?.suggested_notes ?? ""}
          onChange={(event) => {
            setNotes(event.target.value);
            drafts.set(block.id, { notes: event.target.value });
          }}
          onBlur={() => {
            drafts.flush(block.id);
          }}
          className={`${inputClass} py-2`}
        />
        {/* What the notes are for, so the captured lines aren't logged as
            they are by default (idea #872). */}
        <p id={notesHintId} className="text-[12px] text-muted">
          What you did: the time entry&apos;s summary in Autotask. What&apos;s here to start is only what was captured.
        </p>
        {block.isHipaa ? (
          <p className="text-[12px] text-muted">A HIPAA client: don't enter patient details.</p>
        ) : null}
        {unsaved ? (
          <p role="status" className="text-[12px] text-muted">
            Couldn&apos;t save your title and notes yet. They&apos;re kept on this page, and saving is
            tried again when you next type.
          </p>
        ) : null}
      </div>
      {/* Billing and Contract side by side where the form is wide enough, as
          in the popup's right pane on a desktop (ADR 0193); one above the
          other in a narrower one, a phone's. */}
      <div className="@container">
        <div className="flex flex-col gap-2.5 @min-[400px]:grid @min-[400px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-[5px]">
            <label htmlFor={billingId} className="text-xs font-semibold text-muted">
              Billing
            </label>
            <select
              id={billingId}
              value={billing ?? data?.billing ?? ""}
              onChange={(event) => {
                setBilling(event.target.value as Billing);
              }}
              className={`${inputClass} h-10 px-2`}
            >
              {data?.billing ? null : <option value="">Choose</option>}
              {BILLING.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex min-w-0 flex-col gap-[5px]">
            <label htmlFor={contractId} className="text-xs font-semibold text-muted">
              Contract
            </label>
            <select
              id={contractId}
              value={
                contract === null
                  ? data?.contract_id == null
                    ? "none"
                    : String(data.contract_id)
                  : String(contract)
              }
              onChange={(event) => {
                const value = event.target.value;
                setContract(value === "none" ? "none" : Number(value));
              }}
              aria-invalid={contractUnusable}
              className={`${inputClass} h-10 px-2`}
            >
              <option value="none">No contract</option>
              {data?.contract_id != null &&
              !data.contracts.some((c) => c.autotask_contract_id === data.contract_id) ? (
                // The client's default, though time can't go on it that day: shown,
                // and a problem until another is chosen, or none (ADR 0101).
                <option value={String(data.contract_id)}>
                  {`The client's default · ${String(data.contract_id)} (can't be used)`}
                </option>
              ) : null}
              {(data?.contracts ?? []).map((c) => (
                <option key={c.autotask_contract_id} value={String(c.autotask_contract_id)}>
                  {c.name +
                    (c.is_default ? " (default)" : "") +
                    (c.end_date ? ` · until ${contractDay.format(new Date(c.end_date))}` : "")}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* The actions: from 860 px wide, where the popup's right pane scrolls
          on its own (ADR 0193), they stay at its foot while the fields above
          them scroll, as long as the form is in view (#222). The form keeps
          its own box, so it stays a named form for screen readers. Their
          shadow covers the pane's bottom padding, so nothing shows below
          them; a line above them only while they're stuck, where the browser
          can tell. */}
      <div
        ref={actionsRef}
        className="min-[860px]:sticky min-[860px]:bottom-0 min-[860px]:bg-panel min-[860px]:shadow-[0_18px_0_var(--panel)] min-[860px]:[container-type:scroll-state]"
      >
        <div className="flex flex-col gap-2.5 min-[860px]:border-t min-[860px]:border-transparent min-[860px]:pt-2.5 [@container_scroll-state(stuck:bottom)]:border-line">
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={closing}
              aria-describedby={closing ? closeHintId : undefined}
              onChange={(event) => {
                setClose(event.target.checked);
              }}
              className="size-4 accent-brand"
            />
            Close ticket after logging
          </label>
          {closing ? (
            // Completing a ticket is customer-visible: say which, as the
            // button doesn't (idea #871).
            <p id={closeHintId} className="-mt-1.5 text-[12px] text-muted">
              {mode === "new_ticket"
                ? "The new ticket is set to Complete once your time is logged."
                : `${closedName ?? chosenTicket ?? "The ticket"} is set to Complete once your time is logged.`}
            </p>
          ) : null}
          {problems.length > 0 ? (
            <ul aria-label="Before logging" className="flex flex-col gap-1 rounded-row bg-panel-2 px-3 py-2 text-[13px]">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          ) : null}
          {reopening ? (
            <p role="status" className="text-[13px] text-muted">
              {finished !== null
                ? `${closedName ?? "It"} is open again: logging ${block.duration} to it…`
                : `Setting ${closedName ?? "it"} to In Progress in Autotask…`}
            </p>
          ) : null}
          {logRefused !== null ? (
            <p role="alert" className="text-[13px] text-status-needs-client-fg">
              {`Reopened ${closedName ?? chosenTicket ?? "it"}, but couldn't log to it: ${logRefused}`}
            </p>
          ) : null}
          {reopen.isError || reopenFailed ? (
            <div role="alert" className="flex flex-wrap items-center gap-2 text-[13px] text-status-needs-client-fg">
              <span>
                {`Couldn't reopen ${closedName ?? "it"}: ` +
                  (reopenFailed
                    ? (reopenFailed.autotask_errors?.join(" ") ?? reopenFailed.error ?? "Autotask refused it.")
                    : reopen.error instanceof Error
                      ? reopen.error.message
                      : "It couldn't be asked for.")}
              </span>
              {reopenFailed ? (
                <button
                  type="button"
                  disabled={retryReopen.isPending}
                  onClick={() => {
                    retryReopen.mutate(reopenFailed.id);
                  }}
                  className="cursor-pointer rounded-pill border border-line bg-panel px-3 py-1 font-semibold text-text hover:bg-panel-2"
                >
                  Try again
                </button>
              ) : null}
            </div>
          ) : null}
          {preview.isError && !preview.isFetching ? (
            <div role="alert" className="flex flex-wrap items-center gap-2 text-[13px] text-status-needs-client-fg">
              <span>{`Couldn't check what would be written: ${preview.error.message}`}</span>
              <button
                type="button"
                onClick={() => {
                  void preview.refetch();
                }}
                className="cursor-pointer rounded-pill border border-line bg-panel px-3 py-1 font-semibold text-text hover:bg-panel-2"
              >
                Check again
              </button>
            </div>
          ) : null}
          {data?.refusal ? (
            <p role="alert" className="text-[13px] text-status-needs-client-fg">
              {data.refusal}
            </p>
          ) : null}
          {submit.isError ? (
            <p role="alert" className="text-[13px] text-status-needs-client-fg">
              {submit.error instanceof Error ? submit.error.message : "It couldn't be submitted."}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={
                !(ready || (reopenable && reopenId === null)) || submit.isPending || reopen.isPending || reopening
              }
              className="h-11 grow cursor-pointer rounded-pill bg-cta-bg px-5 text-[14px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submit.isPending ? "Logging…" : reopening || reopen.isPending ? "Reopening…" : label}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

/**
 * A closed ticket chosen (P9; ADR 0197): when and by whom it closed, and that
 * logging reopens it. The mockup's amber note.
 */
function ClosedNote({
  ticket,
  timeZone,
}: {
  ticket: NonNullable<SubmissionPreview["closed_ticket"]>;
  timeZone: string;
}) {
  const name = ticket.ticket_number ?? String(ticket.autotask_ticket_id);
  if (ticket.state === "reopening") {
    return (
      <p className="rounded-row bg-panel-2 px-3 py-2 text-[13px] text-muted">
        {`${name} is being reopened: it can be logged to once it's open.`}
      </p>
    );
  }
  const day = ticket.closed_at === null ? null : formatShortDay(todayIn(timeZone, new Date(ticket.closed_at)));
  const since = day === null ? "" : ` since ${day}`;
  const by = ticket.closed_by_name ? `, closed by ${ticket.closed_by_name}` : "";
  const how = ticket.closed_by === "axon_time" ? `Closed by Axon Time${since}${by}.` : `Complete in Autotask${since}${by}.`;
  return (
    <div
      role="note"
      aria-label={`${name} is closed`}
      className="flex flex-col gap-1 rounded-row border border-status-needs-client-line bg-status-needs-client-bg px-3 py-2 text-[13px] text-status-needs-client-fg"
    >
      <p className="font-semibold">{`${name} is closed`}</p>
      <p>{`${how} Logging here reopens it: it goes back to In Progress, then your time is logged to it.`}</p>
    </div>
  );
}

/**
 * Start over's confirm (#831): what Autotask already has from this log stays
 * there, so logging again may add to it.
 */
function startOverText(steps: readonly string[], ticket: string | null): string {
  if (steps.length === 0) {
    return "Nothing reached Autotask. Starting over brings the Log form back, with what you typed, to log this block afresh.";
  }
  const has = steps.map((step) => step.charAt(0).toLowerCase() + step.slice(1)).join(", ");
  return (
    `Autotask already has ${has} from this log, and starting over leaves ${steps.length === 1 ? "it" : "them"} there. ` +
    `Logging again may add a second time entry: remove what's there in Autotask, or log to ${ticket ?? "that ticket"} again.`
  );
}

const ACTIVE: ReadonlySet<Submission["state"]> = new Set([
  "pending",
  "ticket_ready",
  "time_entry_created",
  "note_created",
  "ticket_closed",
]);

/** The done line's colour: the logged green now means closed; open is amber (v0.9.0's P2b). */
const LINE_TONES: Partial<Record<TicketView["state"], string>> = {
  closed: " text-status-logged-fg",
  open: " text-status-needs-client-fg",
  close_failed: " text-status-needs-client-fg",
};

function SubmissionProgress({
  blockId,
  submission,
  ticketState,
  timeZone,
  onSent,
}: {
  blockId: string;
  submission: Submission;
  /** The logged block's ticket, as the day knows it now; null until the day has it. */
  ticketState: TicketView | null;
  timeZone: string;
  onSent: (() => void) | undefined;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const { now } = useDayFacts();
  // In privacy mode, no way into Autotask: a real ticket would open (ADR 0145).
  const privacy = usePrivacy();
  const retry = useMutation({
    mutationFn: () => api.retrySubmission(blockId),
    onSuccess: (retried) => {
      queryClient.setQueryData(["submission", blockId], retried);
      // Try again goes with the failure (#247).
      onSent?.();
    },
    onError: (error) => {
      // Already retried, or gone (someone else, another tab): show it as it is now.
      if (error instanceof ApiError && (error.status === 409 || error.status === 404)) {
        void queryClient.invalidateQueries({ queryKey: ["submission", blockId] });
        void queryClient.invalidateQueries({ queryKey: ["day"] });
      }
    },
  });
  // Start over (#831): a failed log let go, the Log form back. What it wrote
  // to Autotask stays there, so the confirm says what that is.
  const [startingOver, setStartingOver] = useState(false);
  const startOver = useMutation({
    mutationFn: () => api.startOverSubmission(blockId),
    onSuccess: () => {
      queryClient.setQueryData(["submission", blockId], null);
      void queryClient.invalidateQueries({ queryKey: ["submission-preview", blockId] });
      void queryClient.invalidateQueries({ queryKey: ["day"] });
    },
    onError: (error) => {
      // Tried again or started over elsewhere: show it as it is now.
      if (error instanceof ApiError && (error.status === 409 || error.status === 404)) {
        void queryClient.invalidateQueries({ queryKey: ["submission", blockId] });
      }
    },
  });
  // The number techs know a ticket by (T20261002.0018); its ID only until it's known.
  const ticket = submission.ticket_number ?? (submission.ticket_id !== null ? String(submission.ticket_id) : null);
  const done = submission.state === "done";
  const line = done && ticketState !== null ? ticketLine(ticketState, now, timeZone) : null;
  const steps = [
    ticket !== null ? `Ticket ${ticket}` : null,
    submission.time_entry_id !== null ? "Time entry" : null,
    submission.note_id !== null ? "Activity log note" : null,
    submission.chat_note_id !== null ? "Chat note" : null,
    submission.ticket_closed ? "Ticket closed" : null,
  ].filter((step): step is string => step !== null);

  return (
    <section aria-label="Logging to Autotask" className="flex flex-col gap-2 rounded-row bg-panel-2 px-3 py-2.5">
      {/* One status line, so each change is said: the result comes by polling,
          after the click (#247). Autotask's own words for a failure are an
          alert, below. */}
      <p
        role="status"
        className={"text-[13.5px] font-semibold" + (done && ticketState !== null ? (LINE_TONES[ticketState.state] ?? "") : "")}
      >
        {done
          ? `Logged to Autotask: ticket ${ticket ?? ""}` +
            (ticketState === null ? "" : ` · ${LINE_STATES[ticketState.state]}`)
          : submission.state === "failed"
            ? "Autotask didn't take it"
            : "Logging to Autotask…"}
      </p>
      {steps.length > 0 ? (
        <ul aria-label="Written" className="flex flex-col gap-0.5 text-[13px]">
          {steps.map((step) => (
            <li key={step} className="flex gap-1.5">
              <span aria-hidden="true" className="text-status-logged-fg">
                ✓
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {line === null ? null : <p className="text-[12.5px] leading-[1.45] text-muted">{line}</p>}
      {submission.ticket_url !== null && submission.ticket_id !== null && !privacy ? (
        // Autotask's own ExecuteCommand link (ADR 0066); it opens with the
        // viewer's Autotask sign-in and permissions.
        <a
          href={submission.ticket_url}
          target="_blank"
          rel="noopener noreferrer"
          className="self-start text-[13px] font-semibold text-brand-ink underline underline-offset-2"
        >
          {`Open ticket ${ticket ?? String(submission.ticket_id)} in Autotask`}
        </a>
      ) : null}
      {submission.state === "failed" ? (
        <>
            <p role="alert" className="text-[13px] text-status-needs-client-fg">
              {(submission.autotask_errors ?? []).length > 0
                ? (submission.autotask_errors ?? []).join(" ")
                : (submission.error ?? "Something went wrong.")}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={retry.isPending || startOver.isPending}
                onClick={() => {
                  retry.mutate();
                }}
                className="h-10 cursor-pointer rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2 disabled:opacity-50"
              >
                Try again
              </button>
              <button
                type="button"
                aria-expanded={startingOver}
                disabled={retry.isPending || startOver.isPending}
                onClick={() => {
                  setStartingOver((open) => !open);
                }}
                className="h-10 cursor-pointer rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2 disabled:opacity-50"
              >
                Start over…
              </button>
            </div>
            {retry.isError ? (
              <p role="alert" className="text-[13px] text-status-needs-client-fg">
                {`Not tried again: ${retry.error.message}`}
              </p>
            ) : null}
            {startingOver ? (
              <div role="group" aria-label="Start over" className="flex flex-col gap-2 rounded-row border border-line bg-panel px-3 py-2.5 text-[13px]">
                <p>{startOverText(steps, ticket)}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={startOver.isPending}
                    onClick={() => {
                      startOver.mutate();
                    }}
                    className="h-10 cursor-pointer rounded-pill bg-cta-bg px-4 text-[13.5px] font-bold text-cta-fg disabled:opacity-50"
                  >
                    Start over
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStartingOver(false);
                    }}
                    className="h-10 cursor-pointer rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2"
                  >
                    Cancel
                  </button>
                </div>
                {startOver.isError ? (
                  <p role="alert" className="text-status-needs-client-fg">
                    {`Not started over: ${startOver.error.message}`}
                  </p>
                ) : null}
              </div>
            ) : null}
        </>
      ) : null}
    </section>
  );
}

export function LogToAutotask({
  block,
  timeZone,
  start,
  ticketListOpen = false,
  closeAsking = false,
  onActionsResize,
  onSent,
  onLogged,
  actions,
}: {
  block: BlockView;
  /** Dropped on an open ticket: the form starts there (v0.9.0's P4). */
  start?: LogStart | null | undefined;
  /** The form's Ticket field opens at once, on its search (ADR 0194). */
  ticketListOpen?: boolean | undefined;
  /** Close ticket asks at once, a card's "Close ticket…" (ADR 0194). */
  closeAsking?: boolean | undefined;
  /** The tech's own: "open 2 d" counts their days, and "Closed …, 1:14 PM" is their time. */
  timeZone: string;
  /** Told the height of the form's actions, kept at the right pane's foot (#222); 0 once they're gone. */
  onActionsResize?: (height: number) => void;
  /** Told when the block is sent, or sent again: the form, or Try again, is gone (#247). */
  onSent?: () => void;
  /**
   * Told once when what was sent from the popup, or tried again from it, is
   * logged, though the popup was closed and opened again, or stepped away and
   * back, meanwhile: its ticket, as techs know it (ADR 0184). Never for a
   * block already logged when it was opened, or sent by someone else.
   */
  onLogged?: ((ticket: string | null) => void) | undefined;
  /**
   * The popup's Dismiss ▾ and More ⋯ (ADR 0194): shown once, under what this
   * shows, or beside Close ticket while it's there.
   */
  actions?: ReactNode;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const submission = useQuery({
    queryKey: ["submission", block.id],
    queryFn: () => api.getSubmission(block.id),
    refetchInterval: (query) => (query.state.data && ACTIVE.has(query.state.data.state) ? POLL_MS : false),
    retry: retryOnce,
  });
  // Sent, or tried again, from the popup: its logging is the popup's to
  // tell. The popup keeps it, not this form, so a popup closed and opened
  // again, or stepped away and back, while it logs still closes once it's
  // logged (owner report, 2026-10-21, on v0.9.0-rc.1's staging test).
  const sentHere = useSentHere();
  // This form sent it, or has read it still logging since it opened. One
  // opened again only once it's logged stays open: it's seen logged.
  const seenLogging = useRef(false);
  const sent = () => {
    sentHere.add(block.id);
    seenLogging.current = true;
    onSent?.();
  };
  const data = submission.data;
  const done = data?.state === "done";
  const ticket = data ? (data.ticket_number ?? (data.ticket_id !== null ? String(data.ticket_id) : null)) : null;
  // Read since this form opened, not what an earlier one left in the cache.
  const logging = data != null && ACTIVE.has(data.state) && submission.isFetchedAfterMount;
  useEffect(() => {
    if (logging) {
      seenLogging.current = true;
    }
  }, [logging]);
  // The day view's, new on its every render: read through a ref, so a render
  // never runs the effect below again (#838).
  const onLoggedRef = useRef(onLogged);
  useEffect(() => {
    onLoggedRef.current = onLogged;
  });
  // Once, as it becomes done while this form watched it log (#838): a block
  // logged before the popup opened has nothing to read again.
  const handledDone = useRef(false);
  useEffect(() => {
    if (!done) {
      handledDone.current = false;
      return;
    }
    if (handledDone.current || !seenLogging.current) {
      return;
    }
    handledDone.current = true;
    // The block moves to Logged - Open or Closed (section 11.0), and its
    // ticket joins (or, closed, leaves) the tech's open tickets: the tile
    // and the tray (v0.9.0's P5) are read again too.
    void queryClient.invalidateQueries({ queryKey: ["day"] });
    void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    if (sentHere.take(block.id)) {
      onLoggedRef.current?.(ticket);
    }
  }, [done, queryClient, ticket, sentHere, block.id]);

  if (submission.isPending) {
    return <>{actions}</>;
  }
  // A read that fails keeps what was read before (TanStack Query v5): only
  // with nothing read at all is it "couldn't load" (#859). The polling reads
  // it again by itself.
  if (submission.isError && submission.data === undefined) {
    // Never a fresh form: the block may have a submission already (#217; ADR 0053).
    return (
      <>
        <section aria-label="Logging to Autotask" className="flex flex-col gap-2 rounded-row bg-panel-2 px-3 py-2.5">
          <p role="alert" className="text-[13px] text-status-needs-client-fg">
            Couldn&apos;t load this block&apos;s logging to Autotask.
          </p>
          <button
            type="button"
            onClick={() => {
              void submission.refetch();
            }}
            className="h-10 cursor-pointer self-start rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2"
          >
            Load again
          </button>
        </section>
        {actions}
      </>
    );
  }
  if (submission.data) {
    const ticket = block.kind === "logged" ? block.ticket : null;
    return (
      <>
        <SubmissionProgress
          blockId={block.id}
          submission={submission.data}
          ticketState={ticket}
          timeZone={timeZone}
          onSent={sent}
        />
        {ticket !== null && submission.data.state === "done" ? (
          // Close ticket, while it's open (v0.9.0's P3; ADR 0182), the menus beside it.
          <CloseTicket
            ticket={ticket}
            asking={closeAsking}
            where={WHERE_WRITES_GO[submission.data.write_mode]}
            onSent={onSent}
            beside={actions}
          />
        ) : (
          actions
        )}
      </>
    );
  }
  if (block.kind !== "ready") {
    return <>{actions}</>;
  }
  const startHere = start?.blockId === block.id ? start : undefined;
  return (
    <>
      <SubmitForm
        // Dropped again on another ticket: it starts afresh there.
        key={startHere ? `drop:${String(startHere.ticketId)}` : "form"}
        block={block}
        timeZone={timeZone}
        droppedOn={startHere}
        ticketListOpen={ticketListOpen}
        onActionsResize={onActionsResize}
        onSubmitted={(created) => {
          // Before its data: an answer that's done already is this form's too.
          sentHere.add(block.id);
          seenLogging.current = true;
          queryClient.setQueryData(["submission", block.id], created);
          void queryClient.invalidateQueries({ queryKey: ["day"] });
          onSent?.();
        }}
        onReopening={() => {
          // The worker logs it once it's open (#830): its logging is the
          // popup's to tell, as one sent from it.
          sentHere.add(block.id);
          seenLogging.current = true;
        }}
      />
      {actions}
    </>
  );
}
